/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable class-methods-use-this */
import * as Sentry from '@sentry/react';
import { MonitoringAdapter } from './types';

export class SentryAdapter implements MonitoringAdapter {
  initialize?(initOptions: Sentry.BrowserOptions): void {
    Sentry.init(initOptions);
  }

  setUser(
    userId: string | null,
    options: { email?: string; username?: string } = {}
  ): void {
    if (userId) {
      Sentry.setUser({
        id: userId,
        email: options.email,
        username: options.username
      });
    }
  }

  clearUser(): void {
    this.setUser(null);
  }

  setTag(key: string, value: string | number | boolean): void {
    const stringValue = String(value);
    // Optional: auto-redact sensitive keys
    const sensitiveKeys = ['token', 'password', 'secret', 'email'];
    if (sensitiveKeys.some((k) => key.toLowerCase().includes(k))) {
      Sentry.setTag(key, '[REDACTED]');
    } else {
      Sentry.setTag(key, stringValue);
    }
  }

  setTags(tags: Record<string, string | number | boolean>): void {
    Object.entries(tags).forEach(([key, value]) => {
      this.setTag(key, value);
    });
  }

  clearTag(key: string): void {
    Sentry.withScope((scope) => {
      scope.setTag(key, undefined);
    });
  }

  setContext(key: string, context: Record<string, unknown>): void {
    Sentry.setContext(key, context);
  }

  clearContext(key: string): void {
    Sentry.withScope((scope) => {
      scope.setContext(key, {});
    });
  }

  addBreadcrumb(
    message: string,
    options: {
      category?: string;
      level?: 'info' | 'debug' | 'warning' | 'error';
      data?: Record<string, unknown>;
    } = {}
  ): void {
    const { category = 'custom', level = 'info', data } = options;

    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data,
      timestamp: Date.now()
    });
  }

  captureException(
    error: unknown,
    context?: { tags?: Record<string, any>; extra?: Record<string, any> }
  ): void {
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra
    });
  }

  captureMessage(
    message: string,
    level: 'info' | 'debug' | 'warning' | 'error' = 'info',
    context?: { tags?: Record<string, any>; extra?: Record<string, any> }
  ): void {
    Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra
    });
  }

  withScope(callback: (scope: Sentry.Scope) => void): void {
    Sentry.withScope(callback);
  }
}
